import { Attachment } from '@digital-platform/attachments/domain';
import { DomainModelHelper } from '@digital-platform/shared/domain';
import {
  AdditionalSupplement,
  LabAnalyticalAttachment,
  PhysicalComponent,
  RegulatoryQuestion,
  SafetyDataSheet,
  SpecialWasteProfile,
  SpecialWasteProfileMeta,
  WasteProfile,
  WasteProfileCustomerCard,
  WasteProfileMeta,
} from '../interfaces';
import {
  DynamoSafeSpecialWasteProfile,
  MetadataCharacteristics,
} from '../interfaces/special-waste-profile.interface';
import { AdditionalSupplementModel } from './additional-supplement.model';
import { LabAnalyticalAttachmentModel } from './lab-analytical-attachment.model';
import { PhysicalComponentModel } from './physical-component.model';
import { RegulatoryQuestionsModel } from './regulatory-questions.model';
import { SafetyDataSheetModel } from './safety-data-sheet.model';
import { WasteProfileModel } from './waste-profile.model';

export class SpecialWasteProfileModel
  extends WasteProfileModel
  implements SpecialWasteProfile
{
  static readonly DEFAULT_CONFIG: SpecialWasteProfile = {
    ...WasteProfileModel.DEFAULT_CONFIG,
    type: 'special-waste',
    isLegacyProfile: false,
    meta: {
      sections: {
        generator: {
          completed: false,
          valid: false,
        },
        billing: {
          completed: false,
          valid: false,
        },
        authorization: {
          completed: false,
          valid: false,
        },
        wasteStream: {
          completed: false,
          valid: false,
        },
        attachments: {
          completed: false,
          valid: false,
        },
        characteristics: {
          completed: false,
          valid: false,
        },
        destination: {
          completed: false,
          valid: false,
        },
        rcra: {
          completed: false,
          valid: false,
        },
        checkList: {
          completed: false,
          valid: false,
        },
        signature: {
          completed: false,
          valid: false,
        },
      },
    },
    wasteType: null,
    frequency: null,
    stateApprovalWasteCode: '',
    stateApprovalWasteCodeNotApplicable: false,
    methodOfShipment: null,
    otherMethodOfShipment: '',
    metadataCharacteristics: null,
    disposalConsideration: null,
    approvedDisposalConsideration: null,
    isTransferFacilityRequired: false,
    physicalState: null,
    estimatedVolume: null,
    unitOfMeasure: null,
    percentSolids: null,
    percentSolidsLow: null,
    percentSolidsHigh: null,
    percentSolidNotApplicable: false,
    containsFreeLiquids: null,
    phRange: null,
    phLow: null,
    phHigh: null,
    phExact: null,
    phNotApplicable: false,
    flashPoint: null,
    flashPointRange: null,
    flashPointNotApplicable: false,
    labAnalyticalAttachments: [],
    safetyDataSheets: [],
    additionalSupplements: [],
    stateAttachments: [],
    internalOnlyAttachments: [],
    additionalAdminSupplements: [],
    internalAdminAttachments: [],
    thirdPartyAuthAdminAttachment: null,
    stateInternalAdminAttachments: [],
    physicalComponent: null,
    regulatoryQuestions: {
      regulatoryQuestions: [
        RegulatoryQuestionsModel.RCRA1_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.RCRA2_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.RCRA3_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.RCRA4_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.RCRA5_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.RCRA6_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.RCRA7_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.RCRA8_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.RCRA9_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.RCRA10_DEFAULT_CONFIG,
      ],
      rsHandlingQuestions: [
        RegulatoryQuestionsModel.REP1_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.REP2_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.REP3_DEFAULT_CONFIG,
        RegulatoryQuestionsModel.REP4_DEFAULT_CONFIG,
      ],
    },
  };

  // other data
  readonly type: 'special-waste';
  readonly meta: SpecialWasteProfileMeta;

  // waste stream step
  readonly wasteType: 'industrial-process' | 'pollution-control';
  readonly physicalState:
    | 'solid'
    | 'semi-solid'
    | 'powder'
    | 'liquid'
    | 'liquids-in-nonleaking-consumer-size-packaging';
  readonly containsFreeLiquids: boolean;
  readonly percentSolids: string;
  readonly percentSolidsLow: string;
  readonly percentSolidsHigh: string;
  readonly percentSolidNotApplicable: boolean;
  readonly phRange: string;
  readonly phLow: string;
  readonly phHigh: string;
  readonly phExact: string;
  readonly phNotApplicable: boolean;
  readonly flashPoint: string;
  readonly flashPointRange: string;
  readonly flashPointNotApplicable: boolean;
  readonly disposalConsideration:
    | 'landfill'
    | 'solidification'
    | 'biomremediation'
    | 'deep-well';
  readonly approvedDisposalConsideration:
    | 'landfill'
    | 'solidification'
    | 'biomremediation'
    | 'deep-well';
  readonly isTransferFacilityRequired: boolean;
  readonly stateApprovalWasteCode: string;
  readonly stateApprovalWasteCodeNotApplicable: boolean;
  readonly frequency: 'ongoing' | 'one-time';
  readonly estimatedVolume: number;
  readonly unitOfMeasure: string;
  readonly methodOfShipment: 'bulk' | 'drum' | 'bagged' | 'other';
  readonly otherMethodOfShipment: string;

  // attachments step
  readonly labAnalyticalAttachments: LabAnalyticalAttachment[];
  readonly safetyDataSheets: SafetyDataSheet[];
  readonly additionalSupplements: AdditionalSupplement[];
  readonly stateAttachments: Attachment[];
  readonly internalOnlyAttachments: Attachment[];

  // physical characteristics step
  readonly physicalComponent: PhysicalComponent;

  // rcra step
  readonly regulatoryQuestions: {
    readonly regulatoryQuestions: RegulatoryQuestion[];
    readonly rsHandlingQuestions: RegulatoryQuestion[];
  };

  metadataCharacteristics: MetadataCharacteristics;

  constructor(config?: SpecialWasteProfile) {
    super(config);
    this.type = 'special-waste';
    this.metadataCharacteristics = config
      ? config.metadataCharacteristics
      : null;
    this.disposalConsideration = config ? config.disposalConsideration : null;
    this.approvedDisposalConsideration = config
      ? config.approvedDisposalConsideration
      : null;
    this.isTransferFacilityRequired = config
      ? config.isTransferFacilityRequired
      : false;
    this.estimatedVolume = config
      ? DomainModelHelper.ensureDataIsNumber(config.estimatedVolume)
      : null;
    this.frequency = config ? config.frequency : null;
    this.containsFreeLiquids = config
      ? DomainModelHelper.ensureDataIsBoolean(config.containsFreeLiquids)
      : null;
    this.methodOfShipment = config ? config.methodOfShipment : null;
    this.otherMethodOfShipment = config
      ? DomainModelHelper.ensureDataIsString(config.otherMethodOfShipment)
      : null;
    this.unitOfMeasure = config
      ? DomainModelHelper.ensureDataIsString(config.unitOfMeasure)
      : null;
    this.physicalState = config ? config.physicalState : null;
    this.percentSolids = config
      ? DomainModelHelper.ensureDataIsString(config.percentSolids)
      : null;
    this.percentSolidsHigh = config
      ? DomainModelHelper.ensureDataIsString(config.percentSolidsHigh)
      : null;
    this.percentSolidsLow = config
      ? DomainModelHelper.ensureDataIsString(config.percentSolidsLow)
      : null;
    this.percentSolidNotApplicable = config
      ? DomainModelHelper.ensureDataIsBoolean(config.percentSolidNotApplicable)
      : null;
    this.flashPoint = config
      ? DomainModelHelper.ensureDataIsString(config.flashPoint)
      : null;
    this.flashPointRange = config
      ? DomainModelHelper.ensureDataIsString(config.flashPointRange)
      : null;
    this.flashPointNotApplicable = config
      ? DomainModelHelper.ensureDataIsBoolean(config.flashPointNotApplicable)
      : null;
    this.phRange = config
      ? DomainModelHelper.ensureDataIsString(config.phRange)
      : null;
    this.phLow = config
      ? DomainModelHelper.ensureDataIsString(config.phLow)
      : null;
    this.phHigh = config
      ? DomainModelHelper.ensureDataIsString(config.phHigh)
      : null;
    this.phExact = config
      ? DomainModelHelper.ensureDataIsString(config.phExact)
      : null;
    this.phNotApplicable = config
      ? DomainModelHelper.ensureDataIsBoolean(config.phNotApplicable)
      : null;
    this.stateApprovalWasteCode = config
      ? DomainModelHelper.ensureDataIsString(config.stateApprovalWasteCode)
      : null;
    this.stateApprovalWasteCodeNotApplicable = config
      ? DomainModelHelper.ensureDataIsBoolean(
          config.stateApprovalWasteCodeNotApplicable
        )
      : null;
    this.wasteType = config ? config.wasteType : null;

    this.physicalComponent = config
      ? config.physicalComponent
        ? new PhysicalComponentModel(config.physicalComponent)
        : SpecialWasteProfileModel.DEFAULT_CONFIG.physicalComponent
      : SpecialWasteProfileModel.DEFAULT_CONFIG.physicalComponent;

    this.meta = config
      ? config.meta
        ? this.createMeta(config.meta)
        : SpecialWasteProfileModel.DEFAULT_CONFIG.meta
      : SpecialWasteProfileModel.DEFAULT_CONFIG.meta;
    this.labAnalyticalAttachments = config
      ? config.labAnalyticalAttachments
        ? this.createLabAnalyticalAttachments(config.labAnalyticalAttachments)
        : SpecialWasteProfileModel.DEFAULT_CONFIG.labAnalyticalAttachments
      : SpecialWasteProfileModel.DEFAULT_CONFIG.labAnalyticalAttachments;
    this.safetyDataSheets = config
      ? config.safetyDataSheets
        ? this.createSafetyDataSheets(config.safetyDataSheets)
        : SpecialWasteProfileModel.DEFAULT_CONFIG.safetyDataSheets
      : SpecialWasteProfileModel.DEFAULT_CONFIG.safetyDataSheets;
    this.additionalSupplements = config
      ? config.additionalSupplements
        ? this.createAdditionalSupplements(config.additionalSupplements)
        : SpecialWasteProfileModel.DEFAULT_CONFIG.additionalSupplements
      : SpecialWasteProfileModel.DEFAULT_CONFIG.additionalSupplements;
    this.regulatoryQuestions = config
      ? config.regulatoryQuestions
        ? new RegulatoryQuestionsModel(config.regulatoryQuestions)
        : SpecialWasteProfileModel.DEFAULT_CONFIG.regulatoryQuestions
      : SpecialWasteProfileModel.DEFAULT_CONFIG.regulatoryQuestions;
    this.stateAttachments = config ? config.stateAttachments : null;
    this.internalOnlyAttachments = config
      ? config.internalOnlyAttachments
      : null;
  }

  static readonly isSpecialWaste = (
    wasteProfile: WasteProfile | null | WasteProfileCustomerCard
  ): wasteProfile is SpecialWasteProfile =>
    wasteProfile?.type === 'special-waste';

  // excludes the number field
  public getValue(): SpecialWasteProfile {
    return {
      metadataCharacteristics: this.metadataCharacteristics,
      id: this.id,
      schemaVersion: this.schemaVersion,
      type: this.type,
      active: this.active,
      version: this.version,
      meta: this.meta,
      createdBy: this.createdBy,
      createdDate: this.createdDate,
      lastModifiedBy: this.lastModifiedBy,
      lastModifiedDate: this.lastModifiedDate,
      creator: this.creator,
      customerName: this.customerName,
      status: this.status,
      internalStatus: this.internalStatus,
      isLegacyProfile: this.isLegacyProfile,
      isDuplicate: this.isDuplicate,
      duplicatedFromId: this.duplicatedFromId,
      landfill: this.landfill,
      wasteType: this.wasteType,
      frequency: this.frequency,
      processGeneratingWaste: this.processGeneratingWaste,
      generator: this.generator,
      destination: this.destination,
      checkList: this.checkList,
      name: this.name,
      number: this.number,
      offlineApproved: this.offlineApproved,
      offlineExists: this.offlineExists,
      signNowDocUrl: this.signNowDocUrl,
      signNowDocument: this.signNowDocument,
      signatures: this.signatures,
      signedDocument: this.signedDocument,
      docusignData: this.docusignData,
      certificationComments: this.certificationComments,
      certificationName: this.certificationName,
      certificationTitle: this.certificationTitle,
      certificationEmail: this.certificationEmail,
      certificationSignature: this.certificationSignature,
      yearCode: this.yearCode,
      logbookSequenceNumber: this.logbookSequenceNumber,
      logbookId: this.logbookId,
      logbookNotes: this.logbookNotes,
      truxMaterialDescription: this.truxMaterialDescription,
      approvalNotes: this.approvalNotes,
      analyticalRequiredOnNextRecert: this.analyticalRequiredOnNextRecert,
      incompleteNotes: this.incompleteNotes,
      rejectionNotes: this.rejectionNotes,
      salesRepName: this.salesRepName,
      salesRepNumber: this.salesRepNumber,
      coordinator: this.coordinator,
      recertAnalyticalCustomerNote: this.recertAnalyticalCustomerNote,
      approverName: this.approverName,
      incompleteBy: this.incompleteBy,
      rejectedBy: this.rejectedBy,
      submittedDate: this.submittedDate,
      certifiedDate: this.certifiedDate,
      expectedResponseDate: this.expectedResponseDate,
      approvedDate: this.approvedDate,
      rejectedDate: this.rejectedDate,
      incompleteDate: this.incompleteDate,
      expiryDate: this.expiryDate,
      isRecertification: this.isRecertification,
      isChange: this.isChange,
      isExpired: this.isExpired,
      pendingRecertification: this.pendingRecertification,
      pendingChangeRequest: this.pendingChangeRequest,
      log: this.log,
      commentLog: this.commentLog,
      thirdPartyAuth: this.thirdPartyAuth,
      thirdPartyAuthAttachment: this.thirdPartyAuthAttachment,
      additionalAdminSupplements: this.additionalAdminSupplements,
      internalAdminAttachments: this.internalAdminAttachments,
      thirdPartyAuthAdminAttachment: this.thirdPartyAuthAdminAttachment,
      stateInternalAdminAttachments: this.stateInternalAdminAttachments,
      stateApprovalWasteCodeNotApplicable:
        this.stateApprovalWasteCodeNotApplicable,
      additionalComments: this.additionalComments,
      regulatoryIdNumberNotApplicable: this.regulatoryIdNumberNotApplicable,

      // Special Waste Profile Fields
      physicalState: this.physicalState,
      containsFreeLiquids: this.containsFreeLiquids,
      percentSolids: this.percentSolids,
      percentSolidsLow: this.percentSolidsLow,
      percentSolidsHigh: this.percentSolidsHigh,
      percentSolidNotApplicable: this.percentSolidNotApplicable,
      phRange: this.phRange,
      phLow: this.phLow,
      phHigh: this.phHigh,
      phExact: this.phExact,
      phNotApplicable: this.phNotApplicable,
      flashPoint: this.flashPoint,
      flashPointRange: this.flashPointRange,
      flashPointNotApplicable: this.flashPointNotApplicable,
      disposalConsideration: this.disposalConsideration,
      approvedDisposalConsideration: this.approvedDisposalConsideration,
      isTransferFacilityRequired: this.isTransferFacilityRequired,
      stateApprovalWasteCode: this.stateApprovalWasteCode,
      estimatedVolume: this.estimatedVolume,
      unitOfMeasure: this.unitOfMeasure,
      otherMethodOfShipment: this.otherMethodOfShipment,
      labAnalyticalAttachments: this.labAnalyticalAttachments,
      safetyDataSheets: this.safetyDataSheets,
      additionalSupplements: this.additionalSupplements,
      stateAttachments: this.stateAttachments,
      internalOnlyAttachments: this.internalOnlyAttachments,
      physicalComponent: this.physicalComponent,
      regulatoryQuestions: this.regulatoryQuestions,
      methodOfShipment: this.methodOfShipment,
    };
  }

  // TODO: Should this be refactored into (a new) SpecialWasteProfileMetaModel?
  protected createMeta(meta: WasteProfileMeta): SpecialWasteProfileMeta {
    return {
      sections: {
        generator: {
          completed: meta?.sections?.generator?.completed || false,
          valid: meta?.sections?.generator?.valid || false,
        },
        billing: {
          completed: meta?.sections?.billing?.completed || false,
          valid: meta?.sections?.billing?.valid || false,
        },
        authorization: {
          completed: meta?.sections?.authorization?.completed || false,
          valid: meta?.sections?.authorization?.valid || false,
        },
        wasteStream: {
          completed: meta?.sections?.wasteStream?.completed || false,
          valid: meta?.sections?.wasteStream?.valid || false,
        },
        attachments: {
          completed: meta?.sections?.attachments?.completed || false,
          valid: meta?.sections?.attachments?.valid || false,
        },
        characteristics: {
          completed: meta?.sections?.characteristics?.completed || false,
          valid: meta?.sections?.characteristics?.valid || false,
        },
        destination: {
          completed: meta?.sections?.destination?.completed || false,
          valid: meta?.sections?.destination?.valid || false,
        },
        rcra: {
          completed: meta?.sections?.rcra?.completed || false,
          valid: meta?.sections?.rcra?.valid || false,
        },
        checkList: {
          completed: meta?.sections?.checkList?.completed || false,
          valid: meta?.sections?.checkList?.valid || false,
        },
        signature: {
          completed: meta?.sections?.signature?.completed || false,
          valid: meta?.sections?.signature?.valid || false,
        },
      },
    };
  }

  protected createLabAnalyticalAttachments(
    labAnalyticalAttachments: LabAnalyticalAttachment[]
  ): LabAnalyticalAttachment[] {
    if (!Array.isArray(labAnalyticalAttachments)) {
      return [];
    }

    return labAnalyticalAttachments
      .filter((labAnalyticalAttachment) => !!labAnalyticalAttachment)
      .map(
        (labAnalyticalAttachment) =>
          new LabAnalyticalAttachmentModel(labAnalyticalAttachment)
      );
  }

  protected createSafetyDataSheets(
    safetyDataSheets: SafetyDataSheet[]
  ): SafetyDataSheet[] {
    if (!Array.isArray(safetyDataSheets)) {
      return [];
    }

    return safetyDataSheets
      .filter((safetyDataSheet) => !!safetyDataSheet)
      .map((safetyDataSheet) => new SafetyDataSheetModel(safetyDataSheet));
  }

  protected createAdditionalSupplements(
    additionalSupplements: AdditionalSupplement[]
  ): AdditionalSupplement[] {
    if (!Array.isArray(additionalSupplements)) {
      return [];
    }

    return additionalSupplements
      .filter((additionalSupplement) => !!additionalSupplement)
      .map(
        (additionalSupplement) =>
          new AdditionalSupplementModel(additionalSupplement)
      );
  }
}
